import Alert from '@components/alert/alert';
import { TfaSigning } from '@components/tfa/TfaSigning';
import { Button, SendIcon } from '@gurupay/gp-ui-library';
import useMessageInitiateSigning from '@hooks/useMessageInitiateSigning';
import useSignMessage from '@hooks/useSignMessage';
import { isAxiosError } from 'axios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PayloadProps {
	topic: string;
	content: {
		text: string;
	};
	attachments?: File[];
	signature: string;
}

interface MessageFormValues {
	topic?: string;
	content: {
		text: string;
		period_end?: string;
		period_start?: string;
	};
}

interface TFAProps {
	transactionId?: string;
	afterSign: (payload: PayloadProps) => void;
	transactionIds?: string[];
	setSigningInitiated?: (status: boolean) => void;
	topic?: string | undefined;
	message?: string;
	onInitiateSigning?: () => void;
	isValidFields?: boolean;
	files?: File[];
	getValues?: () => MessageFormValues;
	isNewMessage?: boolean;
	isLoading?: boolean;
}

interface MessageSigningError {
	error_code?: string;
	errors?: {
		'content.period_end'?: unknown[];
		'content.period_start'?: unknown[];
	};
}

const KNOWN_SIGNING_ERROR_CODES = new Set(['INVALID_OTP_CODE', 'OTP_COULD_NOT_BE_SENT']);

const getSigningErrorKey = (error: unknown) => {
	if (!isAxiosError<MessageSigningError>(error)) return 'errors.generic';

	const apiError = error.response?.data;
	const hasPeriodIssue =
		Boolean(apiError?.errors?.['content.period_end']?.length) ||
		Boolean(apiError?.errors?.['content.period_start']?.length);

	if (hasPeriodIssue) return 'errors.content.period';
	if (apiError?.error_code && KNOWN_SIGNING_ERROR_CODES.has(apiError.error_code)) {
		return `errors.${apiError.error_code}`;
	}

	return 'errors.generic';
};

const MessageTfa = ({ ...props }: TFAProps) => {
	const { t } = useTranslation('', { keyPrefix: 'tfa' });
	const [signature, setSignature] = useState<string | null>(null);
	const [signingError, setSigningError] = useState<unknown>();
	const [errorSignal, setErrorSignal] = useState(0);
	const [signingKey, setSigningKey] = useState(0);
	const messageInitiateSigning = useMessageInitiateSigning();
	const signMessage = useSignMessage();

	const buildPayload = () => {
		const formData = props.getValues?.() ?? { content: { text: '' } };

		return {
			...formData,
			...(props.topic && { topic: props.topic }),
			...(props.isNewMessage ? { context: 'thread' } : { context: 'message' }),
			attachments: props.files,
			signature,
		};
	};

	const requestOtp = () => {
		props.onInitiateSigning?.();
		const payload = buildPayload();

		if (!payload.topic) {
			return Promise.reject(new Error('Missing message topic'));
		}

		setSigningError(undefined);
		props.setSigningInitiated?.(true);
		const requestPayload = { ...payload, topic: payload.topic };

		return messageInitiateSigning
			.mutateAsync(requestPayload)
			.then((data) => {
				setSignature(data.signature);
				return data;
			})
			.catch((error: unknown) => {
				props.setSigningInitiated?.(false);
				setSigningError(error);
				setErrorSignal((currentSignal) => currentSignal + 1);
				throw error;
			});
	};

	const resetSigningCore = () => {
		setSigningKey((currentKey) => currentKey + 1);
	};

	const handleConfirm = (code: string) => {
		const payload = buildPayload();

		if (!signature || !payload.topic) return;

		const signedPayload = {
			...payload,
			signature,
			topic: payload.topic,
		};

		signMessage.mutate(
			{
				payload: {
					...signedPayload,
					code,
				},
				signature,
			},
			{
				onSuccess: () => {
					props.setSigningInitiated?.(false);
					setSigningError(undefined);
					resetSigningCore();
					props.afterSign(signedPayload);
				},
				onError: (error: unknown) => {
					props.setSigningInitiated?.(false);
					setSigningError(error);
					setErrorSignal((currentSignal) => currentSignal + 1);
				},
			},
		);
	};

	const notification = Boolean(signingError) && (
		<Alert type="error">{t(getSigningErrorKey(signingError))}</Alert>
	);
	const buttons = (
		<Button href="/messages" className="w-full sm:w-max" variant="outlined">
			{t('buttons.cancel')}
		</Button>
	);

	return (
		<div className="w-full">
			<TfaSigning
				key={signingKey}
				buttons={buttons}
				errorSignal={errorSignal}
				isConfirmDisabled={Boolean(
					signMessage.isPending || !signature || props.isLoading,
				)}
				isRequestDisabled={
					props.isLoading ||
					messageInitiateSigning.isPending ||
					signMessage.isPending ||
					!props.isValidFields
				}
				notification={notification}
				onConfirm={handleConfirm}
				onCountdownEnd={() => props.setSigningInitiated?.(false)}
				requestOtp={requestOtp}
				submitButtonProps={{
					children: t('buttons.sendMessage'),
					className: 'flex-row-reverse',
					icon: <SendIcon />,
					variant: 'primary-black',
				}}
			/>
		</div>
	);
};

export { MessageTfa };
